import { Either, ParseResult, Schema } from "effect";
import { parse } from "yaml";
import { PersonasFile, parsePersonasFile } from "./personas.ts";

// ADR-0120 / ADR-0123 — an L2 pack's topic files, their schemas, and the check catalog.
//
// Every language-specific datum lives in one of `TOPICS`, as `L2_<code>/<topic>.yml`. The one source
// of truth for each file's shape is `language-packs/language_packs/schemas/<topic>.schema.json`; the
// Effect schemas below mirror those files key for key, and `test/language-pack-topics.test.ts` fails
// when the two disagree (top-level keys) or when this module and the Python loader
// (`language_packs.pack_blockers`) report different blockers for the shared fixture packs
// (`language-packs/tests/fixtures/topic_files/`).
//
// Missing or empty data is a blocker, never a fallback: a key present but empty fails its schema, and
// an enabled check whose required key is absent is `missing:<check>:<topic>.<key>`.
//
// YAML is read with YAML 1.2 semantics (the `yaml` package's default), so `on`/`no` are strings and a
// YAML 1.1 boolean in `checks.yml` fails the schema in every runtime.

export const TOPICS = [
  "orthography",
  "lexicon",
  "register",
  "grammar",
  "dialect",
  "prompt_examples",
  "personas",
  "rubric",
  "checks",
] as const;
export type Topic = (typeof TOPICS)[number];

/** `L2_<code>` + `lexicon` -> `L2_<code>/lexicon.yml`. */
export const topicFilePath = (l2: string, topic: Topic): string => `L2_${l2}/${topic}.yml`;

// ── The schemas (mirrors of `schemas/<topic>.schema.json`) ──────────────────────
// Every object rejects keys its JSON schema does not list: decoding runs with
// `onExcessProperty: "error"` (see `decodeStrict`), which reaches every nested struct and every record
// key that fails its key pattern. `personas` is the exception — see `PersonasFile`.

const Text = Schema.String.pipe(Schema.minLength(1));
const Texts = Schema.NonEmptyArray(Text);
// A mapping with at least one entry (`minProperties: 1`).
const nonEmptyRecord = <K extends Schema.Schema.Any, V extends Schema.Schema.Any>(
  key: K,
  value: V,
) =>
  Schema.Record({ key, value }).pipe(
    Schema.filter((record) => Object.keys(record).length > 0 || "must have at least one entry"),
  );
const Identifier = Schema.String.pipe(Schema.pattern(/^[a-z][a-z0-9_]*$/));
const GenderWord = Schema.Literal("male", "female");
const GrammaticalGender = Schema.Literal("masculine", "feminine", "neuter");
const CheckId = Schema.String.pipe(Schema.pattern(/^[a-z]+\.[a-z0-9_]+$/));

export const OrthographyFile = Schema.Struct({
  decisive_chars: Schema.optional(Text),
  letters: Schema.optional(Schema.Struct({ upper: Text, lower: Text })),
  opening_marks: Schema.optional(nonEmptyRecord(Text, Text)),
  interrogative_accents: Schema.optional(nonEmptyRecord(Text, Text)),
  spelling_corrections: Schema.optional(nonEmptyRecord(Text, Text)),
  exclamative_exceptions: Schema.optional(Texts),
  midsentence_lowercasable: Schema.optional(Texts),
  strip_chars: Schema.optional(Text),
  marker_chars: Schema.optional(Texts),
});

export const LexiconFile = Schema.Struct({
  function_words: Schema.optional(Texts),
  answer_function_words: Schema.optional(Texts),
  country_names: Schema.optional(Texts),
  speaker_prelude_verbs: Schema.optional(Texts),
  scene_demonyms: Schema.optional(Texts),
  closed_class_words: Schema.optional(nonEmptyRecord(Text, Texts)),
  verb_phrase_openers: Schema.optional(Texts),
  number_words: Schema.optional(
    nonEmptyRecord(Schema.String, Schema.Int.pipe(Schema.nonNegative())),
  ),
  weekdays: Schema.optional(Texts),
  months: Schema.optional(Texts),
  trivial_vocab: Schema.optional(Texts),
  known_set_titles: Schema.optional(Texts),
  meta_dialog_phrases: Schema.optional(Texts),
  leakage_phrases: Schema.optional(nonEmptyRecord(Text, Texts)),
});

export const RegisterFile = Schema.Struct({
  second_person: Schema.optional(Schema.Struct({ informal: Texts, formal: Texts })),
  address_pronouns: Schema.optional(
    Schema.Struct({
      informal: Texts,
      formal: Texts,
      plural_informal: Schema.optional(Texts),
      plural_formal: Schema.optional(Texts),
    }),
  ),
  plural_address: Schema.optional(Texts),
  register_ambiguous_forms: Schema.optional(nonEmptyRecord(Text, Texts)),
  formality_words: Schema.optional(Texts),
  formulae: Schema.optional(
    Schema.Struct({
      first_meeting: Schema.optional(Texts),
      farewell: Schema.optional(Texts),
      intro_cues: Schema.optional(Texts),
      departure_in_progress: Schema.optional(Texts),
    }),
  ),
  guidance: Schema.optional(Text),
});

export const GrammarFile = Schema.Struct({
  gender_pair_exclude: Schema.optional(Texts),
  essential_flagship_verbs: Schema.optional(Texts),
  articles: Schema.optional(Texts),
  article_to_l1: Schema.optional(nonEmptyRecord(Text, Text)),
  article_genders: Schema.optional(nonEmptyRecord(Text, Schema.NonEmptyArray(GrammaticalGender))),
  noun_genders: Schema.optional(nonEmptyRecord(Text, GrammaticalGender)),
  contractions: Schema.optional(nonEmptyRecord(Text, Texts)),
  gendered_forms: Schema.optional(Texts),
  adjective_gender_pairs: Schema.optional(Schema.NonEmptyArray(Schema.Tuple(Text, Text))),
  copulas: Schema.optional(Texts),
  demonstratives: Schema.optional(Texts),
  number_ambiguous_forms: Schema.optional(Texts),
  name_genders: Schema.optional(nonEmptyRecord(Text, GenderWord)),
  role_label_genders: Schema.optional(nonEmptyRecord(Text, GenderWord)),
  gender_agreement: Schema.optional(
    Schema.Struct({
      demonstrative_genders: nonEmptyRecord(Text, GenderWord),
      introduction_copulas: Texts,
      negations: Texts,
      possessives: Texts,
      possessive_copulas: Texts,
      relative_pronoun_genders: nonEmptyRecord(Text, GenderWord),
      relative_complements: Texts,
    }),
  ),
  template_slot_words: Schema.optional(Texts),
  trailing_prepositions: Schema.optional(Texts),
  pedagogy: Schema.optional(nonEmptyRecord(Identifier, Text)),
});

export const DialectFile = Schema.Struct({
  variety: Schema.optional(Text),
  avoided_forms: Schema.optional(Texts),
  substitutions: Schema.optional(nonEmptyRecord(Text, Text)),
  tts_context: Schema.optional(Schema.Struct({ previous_text: Text, next_text: Text })),
});

export const PromptExamplesFile = Schema.Struct({
  examples: Schema.optional(nonEmptyRecord(Identifier, Text)),
});

export const RubricFile = Schema.Struct({
  rules: Schema.NonEmptyArray(
    Schema.Struct({
      id: Schema.String.pipe(Schema.pattern(/^L\.[0-9]+(\.[0-9]+)*$/)),
      title: Text,
      tier: Schema.Literal("CRITICAL", "MUST", "SHOULD", "MAY"),
      weight: Schema.Number.pipe(Schema.positive()),
      scope: Schema.optional(Schema.Literal("within-unit", "cross-unit")),
      body: Text,
      requires_check: Schema.optional(CheckId),
    }),
  ),
});

export const ChecksFile = Schema.Struct({
  checks: Schema.Record({
    key: CheckId,
    value: Schema.Struct({
      enabled: Schema.Boolean,
      samples: Schema.optional(Schema.Struct({ pass: Texts, fail: Texts })),
    }),
  }).pipe(
    // ADR-0123 Decision 6: an enabled check carries the texts it accepts and flags, unless the catalog
    // marks it `samples: false` (nothing to sample, such as a switch).
    Schema.filter((checks) => {
      const unsampled = Object.entries(checks).find(
        ([id, decision]) =>
          decision.enabled && decision.samples === undefined && !isSamplelessCheck(id),
      );
      return (
        unsampled === undefined ||
        `\`${unsampled[0]}\` is enabled, so it needs \`samples\` with \`pass\` and \`fail\` texts`
      );
    }),
  ),
});

/**
 * Each topic's schema. `personas` is ADR-0118's `PersonasFile`, decoded as `parsePersonasFile` always
 * has — it ignores unknown keys and fills defaults (`speed`, `deprecated`), which its JSON schema
 * documents rather than tightens.
 */
export const TOPIC_SCHEMAS = {
  orthography: OrthographyFile,
  lexicon: LexiconFile,
  register: RegisterFile,
  grammar: GrammarFile,
  dialect: DialectFile,
  prompt_examples: PromptExamplesFile,
  personas: PersonasFile,
  rubric: RubricFile,
  checks: ChecksFile,
} as const;

export type TopicData = { readonly [T in Topic]: Schema.Schema.Type<(typeof TOPIC_SCHEMAS)[T]> };

const formatIssues = (error: ParseResult.ParseError): readonly string[] =>
  ParseResult.ArrayFormatter.formatErrorSync(error).map(
    (issue) => `${issue.path.length === 0 ? "" : `${issue.path.join(".")}: `}${issue.message}`,
  );

const decodeStrict =
  <A, I>(schema: Schema.Schema<A, I>) =>
  (content: string): Either.Either<A, readonly string[]> => {
    let raw: unknown;
    try {
      raw = parse(content);
    } catch (error) {
      return Either.left([
        `not valid YAML: ${error instanceof Error ? error.message : String(error)}`,
      ]);
    }
    return Schema.decodeUnknownEither(schema)(raw ?? {}, {
      errors: "all",
      onExcessProperty: "error",
    }).pipe(Either.mapLeft(formatIssues));
  };

const TOPIC_DECODERS: {
  readonly [T in Topic]: (content: string) => Either.Either<TopicData[T], readonly string[]>;
} = {
  orthography: decodeStrict(OrthographyFile),
  lexicon: decodeStrict(LexiconFile),
  register: decodeStrict(RegisterFile),
  grammar: decodeStrict(GrammarFile),
  dialect: decodeStrict(DialectFile),
  prompt_examples: decodeStrict(PromptExamplesFile),
  personas: parsePersonasFile,
  rubric: decodeStrict(RubricFile),
  checks: decodeStrict(ChecksFile),
};

/** One topic file's content, parsed and validated. `Left` carries every problem, human-readable. */
export const decodeTopicFile = <T extends Topic>(
  topic: T,
  content: string,
): Either.Either<TopicData[T], readonly string[]> => TOPIC_DECODERS[topic](content);

// ── The check catalog ───────────────────────────────────────────────────────────
// A copy of `language-packs/language_packs/check_catalog.yml` (code-owned, never pack data). The test
// parses that file and fails when this copy differs.

/** A pack key as a check names it: `<topic>.<key>`, e.g. `lexicon.function_words`. */
export type PackKeyRef = `${Topic}.${string}`;

export interface CatalogCheck {
  readonly kind: "data" | "shape";
  readonly description: string;
  readonly requires: readonly PackKeyRef[];
  readonly runtimes: readonly string[];
  /** Transitional: a pack may leave it undecided (reads as off, not a blocker) until a later change drops the flag. */
  readonly optional?: boolean;
  /** Nothing to sample (a switch): enabled, it needs no `samples` (ADR-0123 Decision 6). */
  readonly samples?: false;
}

export const CHECK_CATALOG = {
  "lexicon.l2_text_detection": {
    kind: "data",
    description:
      "Tells L2 text from L1 text, e.g. L2 prose leaking into an L1 explanation or gloss.",
    requires: ["lexicon.function_words", "orthography.decisive_chars", "orthography.marker_chars"],
    runtimes: ["exercise_audits", "l2_unit_plan", "curriculum_check"],
  },
  "lexicon.proper_noun_gloss_casing": {
    kind: "data",
    description:
      "Keeps country names capitalised. A gloss or chunk that must otherwise be lower-case keeps their capital, and a lowercase country name in prose is flagged and fixed.",
    requires: ["lexicon.country_names"],
    runtimes: ["l2_unit_plan"],
  },
  "lexicon.closed_class_answers": {
    kind: "data",
    description:
      "Accepts a closed-class word as an answer even when it is not a learning-target item.",
    requires: ["lexicon.answer_function_words"],
    runtimes: ["l2_unit_plan", "curriculum_check"],
  },
  "lexicon.closed_class_lexis": {
    kind: "data",
    optional: true,
    description:
      "Lets a learner's gap answer or spoken line use a closed-class word (article, pronoun, preposition, greeting, common verb form) that the unit's vocabulary does not list.",
    requires: ["lexicon.closed_class_words"],
    runtimes: ["exercise_audits"],
  },
  "lexicon.speaker_prelude": {
    kind: "data",
    description:
      'Finds a dialogue line that carries its own speaker attribution ("<name> says - ...").',
    requires: ["lexicon.speaker_prelude_verbs"],
    runtimes: ["l2_unit_plan"],
  },
  "lexicon.scene_demonym_exemption": {
    kind: "data",
    description:
      "Lets an explanation name a language or nationality without being flagged as a leaked scene.",
    requires: ["lexicon.scene_demonyms"],
    runtimes: ["exercise_audits", "l2_unit_plan"],
  },
  "grammar.gender_pair_forms": {
    kind: "shape",
    description:
      "Flags a gendered pair taught as agreement when the two forms are distinct referents.",
    requires: ["grammar.gender_pair_exclude"],
    runtimes: ["exercise_audits", "l2_unit_plan"],
  },
  "grammar.flagship_paradigm_drip": {
    kind: "data",
    description:
      "Makes the essential first verbs drip singular-first instead of arriving as a whole paradigm.",
    requires: ["grammar.essential_flagship_verbs"],
    runtimes: ["exercise_audits", "l2_unit_plan"],
  },
  "rubric.trained_ensemble": {
    kind: "data",
    optional: true,
    samples: false,
    description:
      "Scores exercises with the evaluator ensemble trained on this language's annotated exercises; switch it on only for a language with that training data.",
    requires: [],
    runtimes: [],
  },
  "lexicon.translation_l2_leakage": {
    kind: "data",
    description: "Flags L2 phrases that leaked into an L1 translation field.",
    requires: ["lexicon.leakage_phrases"],
    runtimes: ["curriculum_check", "exercise_audits"],
  },
  "lexicon.known_set_members": {
    kind: "data",
    description:
      "Recognises a module that teaches a closed set (numbers, days, months, the alphabet) by its words.",
    requires: [
      "lexicon.weekdays",
      "lexicon.months",
      "lexicon.number_words",
      "lexicon.known_set_titles",
    ],
    runtimes: ["exercise_audits", "l2_unit_plan"],
  },
  "grammar.gender_form_disambiguation": {
    kind: "data",
    description:
      "Asks for a (masc.)/(fem.)/(neut.) qualifier on a gloss that cannot tell the gendered forms apart.",
    requires: ["grammar.gendered_forms", "orthography.letters", "orthography.strip_chars"],
    runtimes: ["exercise_audits", "l2_unit_plan", "curriculum_check"],
  },
  "register.form_disambiguation": {
    kind: "data",
    description:
      "Asks for a (formal)/(informal) qualifier on a gloss of a register-ambiguous form.",
    requires: [
      "register.register_ambiguous_forms",
      "register.second_person",
      "orthography.letters",
      "orthography.strip_chars",
    ],
    runtimes: ["curriculum_check", "exercise_audits"],
  },
  "grammar.number_form_disambiguation": {
    kind: "data",
    description: "Asks for a (singular)/(plural) qualifier on a gloss of a number-ambiguous form.",
    requires: [
      "grammar.number_ambiguous_forms",
      "register.plural_address",
      "orthography.letters",
      "orthography.strip_chars",
    ],
    runtimes: ["curriculum_check", "exercise_audits"],
  },
  "grammar.referent_gender_agreement": {
    kind: "shape",
    description:
      "Flags a demonstrative, relationship noun, relative pronoun or predicate adjective whose gender disagrees with the person it names.",
    requires: [
      "grammar.gender_agreement",
      "grammar.name_genders",
      "grammar.role_label_genders",
      "grammar.adjective_gender_pairs",
      "grammar.copulas",
      "orthography.letters",
    ],
    runtimes: ["l2_unit_plan", "curriculum_check"],
  },
  "grammar.grammar_kc_classification": {
    kind: "shape",
    description:
      "Classifies a grammar knowledge component as contractions or articles from its wording.",
    requires: ["grammar.articles", "grammar.contractions"],
    runtimes: ["l2_unit_plan"],
  },
  "orthography.interrogative_accents": {
    kind: "data",
    description:
      "Flags an interrogative written without its accent right after an opening mark, and adds the accent.",
    requires: ["orthography.interrogative_accents", "orthography.opening_marks"],
    runtimes: [],
  },
  "orthography.spelling_corrections": {
    kind: "data",
    optional: true,
    description:
      "Flags a word spelled the way another variety writes it, or with a letter written out in plain ASCII, and names the right spelling.",
    requires: ["orthography.spelling_corrections"],
    runtimes: [],
  },
  "orthography.opening_marks": {
    kind: "data",
    description:
      "Flags a sentence whose closing question or exclamation mark lacks its opening mark, a repeated opening mark, and a capital that follows one wrongly; fixes each.",
    requires: ["orthography.opening_marks"],
    runtimes: [],
  },
  "register.greeting_formulae": {
    kind: "data",
    description:
      "Flags a first-meeting formula or a farewell that sits out of position in a dialogue.",
    requires: ["register.formulae"],
    runtimes: [],
  },
  "grammar.noun_article_presence": {
    kind: "data",
    description:
      "Flags a bare noun shown without the article its L1 gloss has; with case-marked articles (`grammar.article_genders`), also an article that cannot mark the noun's gender (`grammar.noun_genders`) in any case.",
    requires: [],
    runtimes: [],
  },
  "grammar.l1_article_matches_l2": {
    kind: "shape",
    description:
      "Flags an L1 gloss whose article does not match the L2 noun it translates; with case-marked articles, by `grammar.article_to_l1`.",
    requires: [],
    runtimes: [],
  },
  "dialect.substitutions": {
    kind: "data",
    description:
      "Flags a form of another variety, such as a regional word, where this pack's variety uses another, and replaces it.",
    requires: ["dialect.substitutions"],
    runtimes: [],
  },
  "register.consistent_address": {
    kind: "shape",
    description: "Flags an exercise that mixes informal and formal address in one speaker's prose.",
    requires: [],
    runtimes: [],
  },
  "dialect.variety_label": {
    kind: "data",
    description: "Keeps the name of the taught variety out of learner-facing instructions.",
    requires: [],
    runtimes: [],
  },
  "orthography.lexicon_lowercase": {
    kind: "shape",
    optional: true,
    description:
      "Flags an L2 word or phrase that starts no sentence (a lexicon item, a mid-sentence blank's options) but starts with a capital, and lowercases it. Off for a language that capitalises nouns.",
    requires: [],
    runtimes: ["exercise_audits", "curriculum_check"],
  },
  "lexicon.answer_language_match": {
    kind: "data",
    optional: true,
    description:
      "Flags a cloze answer in a different language from its carrier, and a choice pool whose options mix languages.",
    requires: [],
    runtimes: ["exercise_audits"],
  },
  "grammar.drill_carrier_inventory": {
    kind: "data",
    optional: true,
    description:
      "Flags an article drill whose carrier noun was never taught as key vocabulary in the unit.",
    requires: [],
    runtimes: ["exercise_audits"],
  },
  "lexicon.cefr_vocabulary_scope": {
    kind: "data",
    optional: true,
    description:
      "Flags an L2 word in exercise prose that no knowledge component so far has taught, closed-class words aside.",
    requires: ["lexicon.closed_class_words"],
    runtimes: [],
  },
} as const satisfies Record<string, CatalogCheck>;
export type CatalogCheckId = keyof typeof CHECK_CATALOG;

/** Whether a pack may leave the check undecided (transitional, see `CatalogCheck.optional`). */
export const isOptionalCheck = (id: CatalogCheckId): boolean =>
  (CHECK_CATALOG as Readonly<Record<string, CatalogCheck>>)[id]?.optional === true;

export const CATALOG_CHECK_IDS = Object.keys(CHECK_CATALOG) as readonly CatalogCheckId[];

/** Whether the catalog marks `id` `samples: false`; an id it does not list is not exempt. */
export const isSamplelessCheck = (id: string): boolean =>
  (CHECK_CATALOG as Readonly<Record<string, CatalogCheck>>)[id]?.samples === false;

const isCatalogCheckId = (id: string): id is CatalogCheckId => id in CHECK_CATALOG;

/** `lexicon.function_words` -> `["lexicon", "function_words"]`. */
export const splitPackKeyRef = (ref: PackKeyRef): readonly [Topic, string] => {
  const dot = ref.indexOf(".");
  const topic = ref.slice(0, dot);
  // Unreachable for a `PackKeyRef`, whose type already names a topic; the guard only narrows.
  if (!isTopic(topic)) throw new Error(`not a pack key: ${ref}`);
  return [topic, ref.slice(dot + 1)];
};

const isTopic = (s: string): s is Topic => (TOPICS as readonly string[]).includes(s);

// ── Blockers ────────────────────────────────────────────────────────────────────

/** Everything that makes a pack's topic files unusable — one per `pack_blockers` code. */
export type TopicBlocker =
  | { readonly _tag: "Invalid"; readonly topic: Topic; readonly issues: readonly string[] }
  | { readonly _tag: "AbsentManifest" }
  | { readonly _tag: "Undecided"; readonly checkId: CatalogCheckId }
  | { readonly _tag: "Unknown"; readonly checkId: string }
  | { readonly _tag: "Missing"; readonly checkId: CatalogCheckId; readonly ref: PackKeyRef };

/** The code both runtimes report for a blocker, e.g. `missing:lexicon.speaker_prelude:lexicon.speaker_prelude_verbs`. */
export const topicBlockerCode = (blocker: TopicBlocker): string => {
  switch (blocker._tag) {
    case "Invalid":
      return `invalid:${blocker.topic}.yml`;
    case "AbsentManifest":
      return "absent:checks.yml";
    case "Undecided":
      return `undecided:${blocker.checkId}`;
    case "Unknown":
      return `unknown:${blocker.checkId}`;
    case "Missing":
      return `missing:${blocker.checkId}:${blocker.ref}`;
  }
};

export interface PackTopics {
  /** The valid topic files the pack holds. An absent or invalid file has no entry. */
  readonly data: Partial<TopicData>;
  /** Sorted by code, exactly as `language_packs.pack_blockers` sorts them. */
  readonly blockers: readonly TopicBlocker[];
}

/** Whether `ref`'s key is present in the pack's topic data. */
export const hasPackKey = (data: Partial<TopicData>, ref: PackKeyRef): boolean => {
  const [topic, key] = splitPackKeyRef(ref);
  const file = data[topic];
  return file !== undefined && key in file;
};

/**
 * Read and judge one L2 pack's topic files. `files` maps pack-relative paths (`L2_<code>/lexicon.yml`)
 * to content; paths outside `L2_<l2>/` are ignored.
 *
 * The rules are `language_packs.pack_blockers`', code for code: an absent topic file is empty, not
 * invalid; an invalid `checks.yml` stops the judgement there; otherwise every catalog check must be
 * decided, no unknown check may be, and an enabled check needs every key it requires (checked only
 * in topic files that are themselves valid, so one broken file is reported once).
 */
export const readPackTopics = (l2: string, files: ReadonlyMap<string, string>): PackTopics => {
  const data: { -readonly [T in Topic]?: TopicData[T] } = {};
  const blockers: TopicBlocker[] = [];
  const invalid = new Set<Topic>();
  const read = <T extends Topic>(topic: T): void => {
    const content = files.get(topicFilePath(l2, topic));
    if (content === undefined) return;
    const decoded = decodeTopicFile(topic, content);
    if (Either.isLeft(decoded)) {
      invalid.add(topic);
      blockers.push({ _tag: "Invalid", topic, issues: decoded.left });
    } else {
      data[topic] = decoded.right;
    }
  };
  for (const topic of TOPICS) read(topic);

  const sorted = (): PackTopics => ({
    data,
    blockers: [...blockers].sort((a, b) => {
      const [x, y] = [topicBlockerCode(a), topicBlockerCode(b)];
      return x < y ? -1 : x > y ? 1 : 0;
    }),
  });

  if (invalid.has("checks")) return sorted();
  const manifest = data.checks?.checks;
  if (manifest === undefined) {
    blockers.push({ _tag: "AbsentManifest" });
    return sorted();
  }
  for (const checkId of CATALOG_CHECK_IDS) {
    if (!(checkId in manifest) && !isOptionalCheck(checkId))
      blockers.push({ _tag: "Undecided", checkId });
  }
  for (const checkId of Object.keys(manifest)) {
    if (!isCatalogCheckId(checkId)) blockers.push({ _tag: "Unknown", checkId });
  }
  for (const checkId of CATALOG_CHECK_IDS) {
    if (manifest[checkId]?.enabled !== true) continue;
    for (const ref of CHECK_CATALOG[checkId].requires) {
      if (!invalid.has(splitPackKeyRef(ref)[0]) && !hasPackKey(data, ref)) {
        blockers.push({ _tag: "Missing", checkId, ref });
      }
    }
  }
  return sorted();
};

/** The sorted blocker codes for one L2 pack — `language_packs.pack_blockers`' TypeScript twin. */
export const packBlockers = (l2: string, files: ReadonlyMap<string, string>): readonly string[] =>
  readPackTopics(l2, files).blockers.map(topicBlockerCode);
